<script setup>
  import { ref, computed } from "vue";
  import { storeToRefs } from "pinia";
  import { Button } from "@/components/ui/button";
  import { Select } from "@/components/episto-ui/select";
  import {
    Dialog,
    DialogContent,
    DialogFooter,
    DialogHeader,
    DialogTitle,
  } from "@/components/ui/dialog";
  import { useDefaultMessagesStore } from "@stores/survey/defaultMessages";
  import { getLanguageOptions } from "@utils/constants/languages";

  defineProps({
    open: { type: Boolean, required: true },
  });

  const emit = defineEmits(["update:open"]);

  const store = useDefaultMessagesStore();
  const { allMessages } = storeToRefs(store);
  const { createMessage } = store;

  const selectedLanguage = ref(null);
  const creating = ref(false);

  const availableLanguages = computed(() => {
    const existing = new Set(allMessages.value.map((dm) => dm.language));
    const allLanguages = getLanguageOptions();
    return Object.values(allLanguages)
      .filter((lang) => !existing.has(lang.value))
      .sort((a, b) => a.label.localeCompare(b.label));
  });

  const handleOpen = (open) => {
    if (open) selectedLanguage.value = null;
    emit("update:open", open);
  };

  const handleCreate = async () => {
    if (!selectedLanguage.value) return;
    creating.value = true;
    const ok = await createMessage(selectedLanguage.value);
    creating.value = false;
    if (ok) emit("update:open", false);
  };
</script>

<template>
  <Dialog
    :open="open"
    @update:open="handleOpen">
    <DialogContent class="sm:max-w-sm">
      <DialogHeader>
        <DialogTitle>Add language</DialogTitle>
      </DialogHeader>

      <div class="flex flex-col gap-2 py-2">
        <Select
          id="language"
          v-model="selectedLanguage"
          :options="availableLanguages"
          placeholder="Select a language"
          filterable />
      </div>

      <DialogFooter>
        <Button
          variant="outline"
          @click="emit('update:open', false)">
          Cancel
        </Button>
        <Button
          :disabled="!selectedLanguage || creating"
          @click="handleCreate">
          Add
        </Button>
      </DialogFooter>
    </DialogContent>
  </Dialog>
</template>
